import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import CheckOutlined from "@mui/icons-material/CheckOutlined";
import ClearOutlined from "@mui/icons-material/ClearOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import AttachFileOutlined from "@mui/icons-material/AttachFileOutlined";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import { useDispatch } from "react-redux";
import { createAttachement, getAttachment } from "slices/attachment";
import NiceImage from "./NiceImage";

// Function to convert file to Base64
const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = (error) => reject(error);
  });
};

const EditableFieldFile = ({
  field,
  initialValue,
  tip,
  allowedMimeTypes,
  maxFileSize,
  isArray,
  uiRenderType,
  onChange = () => {},
  setIsUpdating,
  ...props
}) => {
  const dispatch = useDispatch();
  const [isEditing, setEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [attachmentIds, setAttachmentIds] = useState(() => {
    // Helper function to check if a value is valid
    const isValidId = (id) => {
      return (
        id &&
        typeof id === "string" &&
        id.trim() !== "" &&
        id !== "undefined" &&
        id !== "null" &&
        id !== "Not Set"
      );
    };

    // Initialize attachmentIds with only valid (non-empty) values
    if (isArray) {
      return Array.isArray(initialValue)
        ? initialValue.filter(isValidId)
        : [];
    } else {
      return isValidId(initialValue) ? [initialValue] : [];
    }
  });
  const [attachmentDetails, setAttachmentDetails] = useState([]);

  // Load attachment details when component mounts or value changes
  useEffect(() => {
    const loadAttachmentDetails = async () => {
      // Helper function to check if a value is valid
      const isValidId = (id) => {
        return (
          id &&
          typeof id === "string" &&
          id.trim() !== "" &&
          id !== "undefined" &&
          id !== "null" &&
          id !== "Not Set"
        );
      };

      // Filter out any empty/null/undefined IDs before fetching
      const validIds = attachmentIds.filter(isValidId);
      if (validIds.length > 0) {
        try {
          const details = await Promise.all(
            validIds.map(async (id) => {
              const result = await dispatch(getAttachment({ id })).unwrap();
              // getAttachment returns { attachment: data }
              return result.attachment || result;
            })
          );
          setAttachmentDetails(details.filter((d) => d !== null));
        } catch (error) {
          console.error("Error loading attachments:", error);
          setAttachmentDetails([]);
        }
      } else {
        setAttachmentDetails([]);
      }
    };

    loadAttachmentDetails();
  }, [attachmentIds, dispatch]);

  // Validate file
  const validateFile = (file) => {
    const errors = [];

    // Check file size
    if (maxFileSize) {
      const maxSizeBytes = maxFileSize * 1024 * 1024; // Convert MB to bytes
      if (file.size > maxSizeBytes) {
        errors.push(`File size exceeds ${maxFileSize}MB limit`);
      }
    }

    // Check file type
    if (allowedMimeTypes) {
      const allowedTypes = allowedMimeTypes
        .split(",")
        .map((type) => type.trim().toLowerCase());
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const fileType = file.type.toLowerCase();

      const isAllowed = allowedTypes.some(
        (type) =>
          fileExtension === type ||
          fileType.includes(type) ||
          type === fileExtension
      );

      if (!isAllowed) {
        errors.push(
          `File type not allowed. Allowed types: ${allowedMimeTypes}`
        );
      }
    }

    return errors;
  };

  // Handle file selection
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    const validationErrors = validateFile(file);
    if (validationErrors.length > 0) {
      alert(validationErrors.join("\n"));
      return;
    }

    setIsUploading(true);
    if (setIsUpdating) setIsUpdating(true);

    try {
      // Convert file to base64
      const base64Content = await convertFileToBase64(file);

      // Create attachment
      const attachment = {
        name: file.name,
        category: "StateSet File",
        description: `File for field: ${field}`,
        content: base64Content,
      };

      const result = await dispatch(createAttachement({ attachment })).unwrap();

      // Extract the attachment ID from the response
      const attachmentId = result.response?.data?.id || result.response?.data?._id || result.id;

      if (!attachmentId) {
        throw new Error("Failed to get attachment ID from response");
      }

      // Update attachment IDs
      let newAttachmentIds;
      if (isArray) {
        newAttachmentIds = [...attachmentIds, attachmentId];
      } else {
        newAttachmentIds = [attachmentId];
      }

      setAttachmentIds(newAttachmentIds);

      // Call onChange with the new value
      const valueToSave = isArray ? newAttachmentIds : newAttachmentIds[0];
      onChange(valueToSave);

      setEditing(false);
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload file. Please try again.");
    } finally {
      setIsUploading(false);
      if (setIsUpdating) setIsUpdating(false);
    }
  };

  // Handle file deletion
  const handleDeleteFile = (attachmentId) => {
    const newAttachmentIds = attachmentIds.filter((id) => id !== attachmentId);
    setAttachmentIds(newAttachmentIds);

    // Call onChange with the new value
    const valueToSave = isArray
      ? newAttachmentIds
      : newAttachmentIds.length > 0
      ? newAttachmentIds[0]
      : null;
    onChange(valueToSave);
  };

  // Handle file download
  const handleDownload = (attachment) => {
    if (attachment && attachment.content) {
      // Create a data URL from the base64 content
      const dataUrl = `data:${attachment.mimeType};base64,${attachment.content}`;

      // Create a temporary anchor element and trigger download
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = attachment.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Render file display
  const renderFileDisplay = () => {
    if (attachmentDetails.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No file uploaded
        </Typography>
      );
    }

    // If renderType is <NiceImage>, show only image preview aligned left
    if (uiRenderType === "<NiceImage>") {
      return (
        <Stack spacing={1} alignItems="flex-start">
          {attachmentDetails.map((attachment) => (
            <Box key={attachment.id} sx={{ textAlign: "left" }}>
              <NiceImage maxWidth={300} maxHeight={300}>
                {attachment.id}
              </NiceImage>
            </Box>
          ))}
        </Stack>
      );
    }

    // Otherwise show full file info with download button
    return (
      <Stack spacing={1}>
        {attachmentDetails.map((attachment) => (
          <Box key={attachment.id}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                padding: 1,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                backgroundColor: "#f5f5f5",
              }}
            >
              <AttachFileOutlined fontSize="small" />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {attachment.name}
              </Typography>
              <Tooltip title="Download">
                <IconButton
                  size="small"
                  aria-label="Download file"
                  onClick={() => handleDownload(attachment)}
                >
                  <DownloadOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
              {isEditing && (
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    aria-label="Delete file"
                    color="error"
                    onClick={() => handleDeleteFile(attachment.id)}
                  >
                    <DeleteOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        ))}
      </Stack>
    );
  };

  return (
    <Box>
      <Tooltip title={tip || "Click to upload file"}>
        <Box>
          {!isEditing ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ flex: 1 }}>{renderFileDisplay()}</Box>
              <IconButton
                size="small"
                aria-label="Edit file"
                onClick={() => setEditing(true)}
                disabled={isUploading}
              >
                <EditOutlined fontSize="small" />
              </IconButton>
            </Stack>
          ) : (
            <Stack spacing={2}>
              {renderFileDisplay()}

              <Stack direction="row" spacing={1} alignItems="center">
                <input
                  type="file"
                  id={`file-upload-${field}`}
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                  accept={
                    allowedMimeTypes
                      ? allowedMimeTypes
                          .split(",")
                          .map((type) => `.${type.trim()}`)
                          .join(",")
                      : "*"
                  }
                  disabled={isUploading || (!isArray && attachmentDetails.length > 0)}
                />
                <label htmlFor={`file-upload-${field}`}>
                  <Button
                    variant="contained"
                    component="span"
                    size="small"
                    disabled={isUploading || (!isArray && attachmentDetails.length > 0)}
                    startIcon={
                      isUploading ? (
                        <CircularProgress size={16} />
                      ) : (
                        <AttachFileOutlined />
                      )
                    }
                  >
                    {isUploading
                      ? "Uploading..."
                      : isArray || attachmentDetails.length === 0
                      ? "Upload File"
                      : "Replace File"}
                  </Button>
                </label>

                <Tooltip title="Done">
                  <IconButton
                    size="small"
                    aria-label="Done"
                    onClick={() => setEditing(false)}
                    disabled={isUploading}
                  >
                    <CheckOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Cancel">
                  <IconButton
                    size="small"
                    aria-label="Cancel upload"
                    onClick={() => {
                      setEditing(false);
                      // Reset to initial value
                      setAttachmentIds(
                        isArray
                          ? Array.isArray(initialValue)
                            ? initialValue
                            : []
                          : initialValue
                          ? [initialValue]
                          : []
                      );
                    }}
                    disabled={isUploading}
                  >
                    <ClearOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              {allowedMimeTypes && (
                <Typography variant="caption" color="text.secondary">
                  Allowed types: {allowedMimeTypes}
                </Typography>
              )}
              {maxFileSize && (
                <Typography variant="caption" color="text.secondary">
                  Max size: {maxFileSize}MB
                </Typography>
              )}
            </Stack>
          )}
        </Box>
      </Tooltip>
    </Box>
  );
};

export default EditableFieldFile;
